import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { FormInput } from '../../../../components/forms/FormInput';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import useChart from '../../../../hooks/useChart';
import { Chart } from '../../../../types/Chart/Chart';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { PatientDoc } from '../../../../types/Patient/PatientDoc';
import { PhotoCamera } from '../../../../components/PhotoCamera';
import { FileTypes } from '../../../../types/Files/FileTypes';
import { Button } from '../../../../components/buttons/Button';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { markPreviousPageCompleted } from '../../../../data/navigation/utilities/markPreviousPageCompleted';
import { getNextPage } from '../../../../data/navigation/getNextPage';
import { NavForward } from '../../../../components/buttons/NavForward';
import QRCode from 'react-qr-code';

import { isMobile } from 'react-device-detect';
import { Heading } from '../../../../components/Heading';

import * as Sentry from '@sentry/browser';

export const Route = createFileRoute('/_authenticated/$patientId/photos/idConcierge')({
  component: IdConcierge,
});

function IdConcierge() {
  const { mutate } = useUpdateChartSection();
  const [takingPhoto, setTakingPhoto] = React.useState({
    idFront: false,
    idBack: false,
    insuranceFront: false,
    insuranceBack: false,
    medicareFront: false,
    medicareBack: false,
    portrait: false,
    testDocument: false,
  });
  const { patientId } = Route.useParams<{ patientId: string }>();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const [nextPageURL, setNextPageURL] = React.useState<string | undefined>(undefined);
  const [warned, setWarned] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [photoOneWorking, setPhotoOneWorking] = React.useState(false);
  const [photoTwoWorking, setPhotoTwoWorking] = React.useState(false);

  // WARN MOBILE USERS THAT ENABLING CAMERA DISABLES AUDIO ON CONCIERGE CALL
  React.useEffect(() => {
    if (patientInfo && patientInfo.navType.base === 'concierge' && isMobile && !warned) {
      alert(
        'If you can no longer hear your concierge after using your camera, you may need to return to your Telehealth call and refresh your browser to reconnect with your clinician/concierge',
      );
      setWarned(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warned]);

  // MARK PREVIOUS PAGE AS COMPLETED
  React.useEffect(() => {
    if (patientInfo && patientInfo.navObj) {
      markPreviousPageCompleted({ currentPageName: 'idConcierge', patientInfo, value: true });
    }
  }, [patientInfo]);

  // GET NEXT PAGE URL
  React.useEffect(() => {
    if (chart && patientInfo.navObj) {
      (async () => {
        const awaitedUrl = await getNextPage({
          currentPage: 'idConcierge',
          chart: chart,
          patientInfo: patientInfo,
        });
        setNextPageURL(awaitedUrl);
      })();
    }
  }, [chart, patientInfo]);

  // WRITE FORM DATA TO FIRESTORE
  const handleIdInfoUpdate = () => {
    mutate(
      {
        patientId: patientId,
        docData: {
          PHOTO_ID: {
            issuingState: idForm.state.values.issuingState,
            licenseNumber: idForm.state.values.idNumber,

            photoIdentificationFrontURI: chart?.PHOTO_ID?.photoIdentificationFrontURI
              ? chart.PHOTO_ID.photoIdentificationFrontURI
              : '',
            photoIdentificationBackURI: chart?.PHOTO_ID?.photoIdentificationBackURI
              ? chart.PHOTO_ID.photoIdentificationBackURI
              : '',
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
          setIsSubmitted(true);
        },
        onError: (error) => {
          Sentry.captureException(error); // SEND ERROR TO SENTRY
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // INITIALIZE FORM
  const idForm = useForm({
    defaultValues: {
      issuingState: chart?.PHOTO_ID?.issuingState ? chart.PHOTO_ID.issuingState : '',
      idNumber: chart?.PHOTO_ID?.licenseNumber ? chart?.PHOTO_ID.licenseNumber : '',
    },
    onSubmit: async () => {
      await handleIdInfoUpdate();
    },
  });

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId && chart?.PATIENT_INFO_1) {
    return (
      <>
        <div id="photo-upload-page" className="flex flex-col">
          <Heading id="photo-upload-page-header" textContent="Submit ID" />
          <div className="my-4">
            <div id="front-id-container" className="flex flex-col items-center">
              <p className="text-xl font-bold">Government issued ID (front):</p>
              {chart?.PHOTO_ID?.photoIdentificationFrontURI && takingPhoto.idFront === false ? (
                <div className="flex flex-row items-center justify-center">
                  <img src={chart.PHOTO_ID.photoIdentificationFrontURI} style={{ width: `75%` }} alt="ID Front" />
                  {/* <Button
                    id="retake-gov-id-front"
                    type="button"
                    textContent="Retake Photo"
                    onClick={() => {
                      setTakingPhoto({ ...takingPhoto, idFront: true });
                    }}
                  /> */}
                </div>
              ) : (
                takingPhoto.idFront === false && (
                  <>
                    <Button
                      id="take-gov-id-front"
                      type="button"
                      textContent="Take Photo"
                      onClick={() => {
                        setTakingPhoto({ ...takingPhoto, idFront: true });
                      }}
                    />
                  </>
                )
              )}
              {takingPhoto.idFront && (
                <PhotoCamera
                  type={FileTypes.idFront}
                  docToUpdate="PHOTO_ID"
                  style={'card'}
                  bucket={'patient'}
                  path={`user/${patientId}/images/photoIdentificationFront`}
                  patientId={patientId}
                  hideCamera={() => {
                    setPhotoOneWorking(false);
                    setTakingPhoto({ ...takingPhoto, idFront: false });
                  }}
                  working={photoOneWorking}
                  setWorking={setPhotoOneWorking}
                />
              )}
            </div>
            <div id="rear-id-container" className="flex flex-col items-center">
              <p className="text-xl font-bold">Government Issued ID (rear):</p>
              {chart?.PHOTO_ID?.photoIdentificationBackURI && takingPhoto.idBack === false ? (
                <div className="flex flex-row items-center justify-center">
                  <img src={chart.PHOTO_ID.photoIdentificationBackURI} style={{ width: `75%` }} alt="ID Back" />
                  {/* <Button
                    id="retake-gov-id-back"
                    type="button"
                    textContent="Retake Photo"
                    onClick={() => {
                      setTakingPhoto({ ...takingPhoto, idBack: true });
                    }}
                  /> */}
                </div>
              ) : (
                takingPhoto.idBack === false && (
                  <>
                    <Button
                      id="take-gov-id-back"
                      type="button"
                      textContent="Take Photo"
                      onClick={() => setTakingPhoto({ ...takingPhoto, idBack: true })}
                    />
                  </>
                )
              )}
              {takingPhoto.idBack && (
                <PhotoCamera
                  type={FileTypes.idBack}
                  docToUpdate="PHOTO_ID"
                  style={'card'}
                  bucket={'patient'}
                  path={`user/${patientId}/images/photoIdentificationBack`}
                  patientId={patientId}
                  hideCamera={() => {
                    setPhotoTwoWorking(false);
                    setTakingPhoto({ ...takingPhoto, idBack: false });
                  }}
                  working={photoTwoWorking}
                  setWorking={setPhotoTwoWorking}
                />
              )}
              {patientInfo && patientInfo.conciergeNavigator && patientInfo.conciergeNavigator.captureId && (
                <div className="my-4 flex w-7/12 flex-col items-center">
                  <QRCode
                    size={32}
                    style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                    value={patientInfo.conciergeNavigator.captureId.url}
                    viewBox={`0 0 32 32`}
                  />
                  <p className="text-center text-xl">Or scan QR Code with mobile device to upload.</p>
                  <p className="text-xl">Good for one use only.</p>
                </div>
              )}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  idForm.handleSubmit();
                }}
              >
                <div className="flex flex-col">
                  <idForm.Field
                    name="idNumber"
                    children={(field) => (
                      <>
                        <FormInput
                          label="ID Number"
                          id="id-number"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={false}
                        />
                      </>
                    )}
                  />
                  <idForm.Field
                    name="issuingState"
                    children={(field) => (
                      <>
                        <FormInput
                          label="Issuing State"
                          id="issuing-state"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={false}
                        />
                      </>
                    )}
                  />
                </div>
              </form>
              <Button id="submit-id-info" type="submit" textContent="Submit" onClick={handleIdInfoUpdate} />
            </div>
          </div>
          <div id="nav" className="my-4 flex flex-col items-center">
            {nextPageURL && isSubmitted && (
              <NavForward
                to={nextPageURL}
                disabled={
                  !chart?.PHOTO_ID?.issuingState ||
                  !chart?.PHOTO_ID?.licenseNumber ||
                  !chart?.PHOTO_ID?.photoIdentificationBackURI
                }
              />
            )}
          </div>
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
